<script setup>
import { ref } from 'vue';
import { useRoute } from 'vue-router';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import { useAlert } from 'dashboard/composables';
import AiAPI from 'custom/api/ai';
import { useLocoI18n } from 'custom/composables/useLocoI18n';
import { learnedFromRoute } from './helpers';

// Assistant › Knowledge › Learn from your team: Q&A pairs suggested from conversations the team resolved, which
// become knowledge once approved. New resolved conversations can be read as they happen, past ones on demand.
const props = defineProps({
  assistant: { type: Object, required: true },
  suggestions: { type: Array, required: true },
});
const emit = defineEmits(['updated', 'learning', 'approve', 'edit', 'dismiss']);

const { t } = useLocoI18n();
const route = useRoute();

const DAY_OPTIONS = [7, 30, 90];

const days = ref(30);
const isLearning = ref(false);
const isApprovingAll = ref(false);

const errorMessage = error => error.response?.data?.error || error.message;

const toggleSetting = async (name, value) => {
  try {
    const { data } = await AiAPI.updateAssistant(props.assistant.id, {
      settings: { [name]: value },
    });
    emit('updated', data);
  } catch (error) {
    useAlert(errorMessage(error));
  }
};

const approveAll = async () => {
  isApprovingAll.value = true;
  try {
    await AiAPI.approveSuggestions(props.assistant.id);
    useAlert(t('LOCO.AI.LEARN.APPROVING_ALL'));
    emit('learning');
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isApprovingAll.value = false;
  }
};

const learn = async () => {
  isLearning.value = true;
  try {
    const { data } = await AiAPI.learn(props.assistant.id, days.value);
    useAlert(
      data.queued
        ? t('LOCO.AI.LEARN.QUEUED', data.queued)
        : t('LOCO.AI.LEARN.NOTHING')
    );
    if (data.queued) emit('learning');
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isLearning.value = false;
  }
};
</script>

<template>
  <section
    class="flex flex-col gap-4 p-4 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak"
  >
    <div class="flex items-start gap-3">
      <Icon
        icon="i-lucide-graduation-cap"
        class="size-5 mt-0.5 flex-shrink-0 text-n-iris-11"
      />
      <div class="flex flex-col gap-1 flex-1 min-w-0">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('LOCO.AI.LEARN.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">{{ t('LOCO.AI.LEARN.HELP') }}</p>
      </div>
    </div>

    <label class="flex items-center justify-between gap-4 text-sm">
      <span class="text-n-slate-12">{{ t('LOCO.AI.LEARN.AUTO') }}</span>
      <Switch
        :model-value="assistant.settings.learn_from_conversations"
        @update:model-value="
          value => toggleSetting('learn_from_conversations', value)
        "
      />
    </label>

    <label class="flex items-start justify-between gap-4 text-sm">
      <span class="flex flex-col gap-0.5">
        <span class="text-n-slate-12">{{
          t('LOCO.AI.LEARN.AUTO_APPROVE')
        }}</span>
        <span class="text-xs text-n-slate-11">
          {{ t('LOCO.AI.LEARN.AUTO_APPROVE_HELP') }}
        </span>
      </span>
      <Switch
        :model-value="assistant.settings.auto_approve_learned"
        @update:model-value="
          value => toggleSetting('auto_approve_learned', value)
        "
      />
    </label>

    <div class="flex flex-wrap items-center gap-2">
      <select v-model="days" class="!mb-0 !w-auto text-sm">
        <option v-for="option in DAY_OPTIONS" :key="option" :value="option">
          {{ t('LOCO.AI.LEARN.DAYS', { n: option }) }}
        </option>
      </select>
      <Button
        sm
        faded
        slate
        icon="i-lucide-book-open-check"
        :label="t('LOCO.AI.LEARN.RUN')"
        :is-loading="isLearning"
        @click="learn"
      />
    </div>

    <div v-if="suggestions.length" class="flex flex-col gap-2">
      <div class="flex flex-wrap items-start justify-between gap-2">
        <div class="flex flex-col gap-0.5">
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ t('LOCO.AI.LEARN.REVIEW', suggestions.length) }}
          </h4>
          <p class="text-xs text-n-amber-11">
            {{ t('LOCO.AI.LEARN.REVIEW_HELP') }}
          </p>
        </div>
        <Button
          sm
          faded
          teal
          icon="i-lucide-check-check"
          :label="t('LOCO.AI.LEARN.APPROVE_ALL')"
          :is-loading="isApprovingAll"
          @click="approveAll"
        />
      </div>
      <ul class="flex flex-col divide-y divide-n-weak">
        <li
          v-for="suggestion in suggestions"
          :key="suggestion.id"
          class="flex items-start gap-3 py-3"
        >
          <div class="flex flex-col gap-1 min-w-0 flex-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ suggestion.title }}
            </span>
            <span class="text-sm text-n-slate-11 whitespace-pre-wrap">
              {{ suggestion.content }}
            </span>
            <router-link
              v-if="suggestion.settings?.conversation_display_id"
              :to="learnedFromRoute(route.params.accountId, suggestion)"
              class="self-start text-xs text-n-blue-11 hover:underline"
            >
              {{
                t('LOCO.AI.LEARN.FROM', {
                  id: suggestion.settings.conversation_display_id,
                })
              }}
            </router-link>
          </div>
          <div class="flex items-center gap-1 flex-shrink-0">
            <Button
              xs
              faded
              teal
              icon="i-lucide-check"
              :label="t('LOCO.AI.LEARN.APPROVE')"
              @click="emit('approve', suggestion)"
            />
            <Button
              xs
              ghost
              slate
              icon="i-lucide-pencil"
              :title="t('LOCO.AI.KNOWLEDGE.EDIT')"
              @click="emit('edit', suggestion)"
            />
            <Button
              xs
              ghost
              ruby
              icon="i-lucide-x"
              :title="t('LOCO.AI.LEARN.DISMISS')"
              @click="emit('dismiss', suggestion)"
            />
          </div>
        </li>
      </ul>
    </div>
  </section>
</template>
